import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Row,
  Col,
  Statistic,
  Space,
  Tag,
  Popconfirm,
  Tabs,
  Select,
  DatePicker,
  Timeline,
  Alert
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SafetyOutlined,
  SecurityScanOutlined,
  KeyOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import axios from 'axios';

// Create axios instance for JWT authentication (for admin endpoints)
const adminApi = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL || '/api',
  timeout: 10000,
});

// Request interceptor to add JWT token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const { TabPane } = Tabs;

const Security = () => {
  const [loading, setLoading] = useState(false);
  const [whitelist, setWhitelist] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogStats, setAuditLogStats] = useState({});

  // Get current user role
  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = userData?.role;
  console.log('Current user role:', userRole);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingWhitelist, setEditingWhitelist] = useState(null);
  const [form] = Form.useForm();

  // Pagination states
  const [whitelistPagination, setWhitelistPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const [auditLogPagination, setAuditLogPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  // Filter states
  const [whitelistFilters, setWhitelistFilters] = useState({});
  const [auditLogFilters, setAuditLogFilters] = useState({});
  const [signatureModalVisible, setSignatureModalVisible] = useState(false);
  const [signatureForm] = Form.useForm();
  const [signatureValidationResult, setSignatureValidationResult] = useState(null);
  const [signatureLoading, setSignatureLoading] = useState(false);

  useEffect(() => {
    fetchWhitelist();
    fetchAuditLogs();
    fetchAuditLogStats();
  }, []);

  const fetchWhitelist = async (page = 1, filters = {}) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: whitelistPagination.pageSize,
        ...filters
      };

      console.log('Fetching IP whitelist with params:', params);
      const response = await adminApi.get('/security/ip-whitelist', { params });
      console.log('IP whitelist response:', response.data);

      setWhitelist(response.data.whitelist);
      setWhitelistPagination({
        current: page,
        pageSize: whitelistPagination.pageSize,
        total: response.data.pagination.total
      });
    } catch (error) {
      message.error('Gagal memuat IP Whitelist: ' + (error.response?.data?.error || error.message));
      console.error('Fetch whitelist error:', error.response || error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async (page = 1, filters = {}) => {
    try {
      const params = {
        page,
        limit: auditLogPagination.pageSize,
        ...filters
      };

      const response = await adminApi.get('/security/audit-logs', { params });

      setAuditLogs(response.data.logs);
      setAuditLogPagination({
        current: page,
        pageSize: auditLogPagination.pageSize,
        total: response.data.pagination.total
      });
    } catch (error) {
      message.error('Gagal memuat Audit Logs');
      console.error('Fetch audit logs error:', error);
    }
  };

  const fetchAuditLogStats = async () => {
    try {
      const response = await adminApi.get('/security/audit-logs', {
        params: { limit: 1 }
      });

      // Mock stats for now - could be enhanced with real stats endpoint
      setAuditLogStats({
        total: auditLogPagination.total || 0,
        today: 12,
        thisWeek: 67,
        successRate: 94.5
      });
    } catch (error) {
      console.error('Fetch audit log stats error:', error);
    }
  };

  const handleAddWhitelist = () => {
    setEditingWhitelist(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditWhitelist = (record) => {
    setEditingWhitelist(record);
    form.setFieldsValue({
      ip_address: record.ip_address,
      description: record.description,
      is_active: record.is_active
    });
    setModalVisible(true);
  };

  const handleSubmitWhitelist = async (values) => {
    try {
      if (editingWhitelist) {
        await adminApi.put(`/security/ip-whitelist/${editingWhitelist.id}`, values);
        message.success('IP Whitelist berhasil diperbarui');
      } else {
        await adminApi.post('/security/ip-whitelist', values);
        message.success('IP berhasil ditambahkan ke whitelist');
      }

      setModalVisible(false);
      fetchWhitelist(whitelistPagination.current, whitelistFilters);
    } catch (error) {
      message.error(error.response?.data?.error || 'Gagal menyimpan IP Whitelist');
      console.error('Submit whitelist error:', error);
    }
  };

  const handleDeleteWhitelist = async (id) => {
    try {
      await adminApi.delete(`/security/ip-whitelist/${id}`);
      message.success('IP berhasil dihapus dari whitelist');
      fetchWhitelist(whitelistPagination.current, whitelistFilters);
    } catch (error) {
      message.error(error.response?.data?.error || 'Gagal menghapus IP');
      console.error('Delete whitelist error:', error);
    }
  };

  const validateSignature = async () => {
    try {
      setSignatureModalVisible(true);
      signatureForm.resetFields();
      setSignatureValidationResult(null);
    } catch (error) {
      message.error('Gagal memuat validasi signature');
    }
  };

  const handleSignatureValidation = async (values) => {
    try {
      setSignatureLoading(true);
      const response = await adminApi.post('/security/validate-signature', {
        payload: values.payload,
        signature: values.signature,
        secret_key: values.secret_key
      });

      setSignatureValidationResult(response.data);
      message.success(response.data.message);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Signature validation failed';
      setSignatureValidationResult({
        valid: false,
        message: errorMsg,
        error: true
      });
      message.error(errorMsg);
    } finally {
      setSignatureLoading(false);
    }
  };

  const whitelistColumns = [
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
      render: (ip) => <Tag color="blue">{ip}</Tag>
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (desc) => desc || '-'
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Created By',
      dataIndex: 'created_by_username',
      key: 'created_by_username',
      render: (username) => username || 'System'
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString('id-ID')
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditWhitelist(record)}
            size="small"
          >
            Edit
          </Button>
          <Popconfirm
            title="Hapus IP dari whitelist?"
            description="Apakah Anda yakin ingin menghapus IP ini?"
            onConfirm={() => handleDeleteWhitelist(record.id)}
            okText="Ya"
            cancelText="Tidak"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Hapus
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const auditLogColumns = [
    {
      title: 'Timestamp',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString('id-ID'),
      width: 180
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action) => <Tag color="blue">{action}</Tag>
    },
    {
      title: 'Resource',
      dataIndex: 'resource',
      key: 'resource',
      render: (resource) => resource || '-'
    },
    {
      title: 'User',
      dataIndex: 'user_username',
      key: 'user_username',
      render: (username, record) => (
        <div>
          <div>{username || 'Anonymous'}</div>
          {record.user_full_name && (
            <small style={{ color: '#666' }}>{record.user_full_name}</small>
          )}
        </div>
      )
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
      render: (ip) => <Tag color="default">{ip}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'success' ? 'green' : status === 'failed' ? 'red' : 'orange'}>
          {status.toUpperCase()}
        </Tag>
      )
    }
  ];

  return (
    <div className="fade-in">
      <div style={{ marginBottom: '40px' }}>
        <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--white)' }}>
          Security Management
        </h1>
        <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '16px' }}>
          Kelola keamanan sistem, IP Whitelist, dan pantau audit logs
        </p>
      </div>

      {/* Security Stats */}
      <Row gutter={[24, 24]} style={{ marginBottom: '40px' }}>
        <Col xs={24} sm={12} md={6}>
          <div className="stat-card">
            <div className="modern-statistic">
              <Statistic
                title="Total Audit Logs"
                value={auditLogStats.total}
                prefix={<HistoryOutlined style={{ color: 'var(--info)' }} />}
                valueStyle={{ color: 'var(--info)', fontWeight: 700 }}
              />
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div className="stat-card success">
            <div className="modern-statistic">
              <Statistic
                title="Hari Ini"
                value={auditLogStats.today}
                prefix={<SafetyOutlined style={{ color: 'var(--success)' }} />}
                valueStyle={{ color: 'var(--success)', fontWeight: 700 }}
              />
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div className="stat-card info">
            <div className="modern-statistic">
              <Statistic
                title="Minggu Ini"
                value={auditLogStats.thisWeek}
                prefix={<SecurityScanOutlined style={{ color: 'var(--info)' }} />}
                valueStyle={{ color: 'var(--info)', fontWeight: 700 }}
              />
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div className="stat-card warning">
            <div className="modern-statistic">
              <Statistic
                title="Success Rate"
                value={auditLogStats.successRate}
                precision={1}
                suffix="%"
                prefix={<KeyOutlined style={{ color: 'var(--warning)' }} />}
                valueStyle={{ color: 'var(--warning)', fontWeight: 700 }}
              />
            </div>
          </div>
        </Col>
      </Row>

      {/* Main Content */}
      <Tabs defaultActiveKey="whitelist" type="card">
        <TabPane
          tab={
            <span>
              <SafetyOutlined />
              IP Whitelist
            </span>
          }
          key="whitelist"
        >
          <div className="chart-container">
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--dark)' }}>
                Manajemen IP Whitelist
              </h3>
              <Space>
                <Button
                  type="primary"
                  icon={<KeyOutlined />}
                  onClick={validateSignature}
                >
                  Validasi Signature
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddWhitelist}
                >
                  Tambah IP
                </Button>
              </Space>
            </div>

            <Table
              columns={whitelistColumns}
              dataSource={whitelist}
              rowKey="id"
              loading={loading}
              pagination={{
                current: whitelistPagination.current,
                pageSize: whitelistPagination.pageSize,
                total: whitelistPagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} dari ${total} item`,
                onChange: (page, pageSize) => {
                  setWhitelistPagination(prev => ({ ...prev, current: page, pageSize }));
                  fetchWhitelist(page, whitelistFilters);
                }
              }}
              className="modern-table"
            />
          </div>
        </TabPane>

        <TabPane
          tab={
            <span>
              <HistoryOutlined />
              Audit Logs
            </span>
          }
          key="auditlogs"
        >
          <div className="chart-container">
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--dark)' }}>
              Audit Trail
            </h3>

            <Table
              columns={auditLogColumns}
              dataSource={auditLogs}
              rowKey="id"
              loading={loading}
              pagination={{
                current: auditLogPagination.current,
                pageSize: auditLogPagination.pageSize,
                total: auditLogPagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} dari ${total} item`,
                onChange: (page, pageSize) => {
                  setAuditLogPagination(prev => ({ ...prev, current: page, pageSize }));
                  fetchAuditLogs(page, auditLogFilters);
                }
              }}
              className="modern-table"
              scroll={{ x: true }}
            />
          </div>
        </TabPane>
      </Tabs>

      {/* Signature Validation Modal */}
      <Modal
        title="Signature Validation Tool"
        open={signatureModalVisible}
        onCancel={() => setSignatureModalVisible(false)}
        footer={null}
        width={800}
      >
        <Alert
          message="Validasi Signature B2B Callback"
          description="Gunakan tool ini untuk memvalidasi signature yang diterima dari callback Arendo. Masukkan payload, signature, dan secret key untuk verifikasi."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form
          form={signatureForm}
          layout="vertical"
          onFinish={handleSignatureValidation}
        >
          <Form.Item
            label="Payload (JSON)"
            name="payload"
            rules={[
              { required: true, message: 'Payload harus diisi!' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  try {
                    JSON.parse(value);
                    return Promise.resolve();
                  } catch {
                    return Promise.reject(new Error('Format JSON tidak valid!'));
                  }
                }
              }
            ]}
          >
            <Input.TextArea
              placeholder='{"transaction_id": "TRX001", "amount": 100000, "status": "success"}'
              rows={4}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <Form.Item
            label="Signature"
            name="signature"
            rules={[{ required: true, message: 'Signature harus diisi!' }]}
          >
            <Input
              placeholder="Masukkan signature yang diterima"
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <Form.Item
            label="Secret Key (Opsional)"
            name="secret_key"
            help="Kosongkan untuk menggunakan default secret key"
          >
            <Input.Password
              placeholder="Masukkan secret key (opsional)"
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          {signatureValidationResult && (
            <Alert
              message={
                signatureValidationResult.valid
                  ? "✅ Signature Valid"
                  : signatureValidationResult.error
                    ? "❌ Validation Error"
                    : "❌ Signature Invalid"
              }
              description={signatureValidationResult.message}
              type={signatureValidationResult.valid ? "success" : "error"}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {signatureValidationResult?.details && (
            <Card size="small" title="Validation Details" style={{ marginBottom: 16 }}>
              <div style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                <p><strong>Received:</strong> {signatureValidationResult.details.received_signature}</p>
                <p><strong>Expected:</strong> {signatureValidationResult.details.expected_signature}</p>
                <p><strong>Secret Used:</strong> {signatureValidationResult.details.secret_used}</p>
                <details>
                  <summary><strong>Payload:</strong></summary>
                  <pre style={{ fontSize: '11px', marginTop: '8px' }}>
                    {JSON.stringify(signatureValidationResult.details.payload, null, 2)}
                  </pre>
                </details>
              </div>
            </Card>
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setSignatureModalVisible(false)}>
                Tutup
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={signatureLoading}
                icon={<SafetyOutlined />}
              >
                Validasi Signature
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add/Edit Whitelist Modal */}
      <Modal
        title={editingWhitelist ? 'Edit IP Whitelist' : 'Tambah IP ke Whitelist'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmitWhitelist}
        >
          <Form.Item
            label="IP Address"
            name="ip_address"
            rules={[
              { required: true, message: 'IP Address harus diisi' },
              {
                pattern: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$|^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/,
                message: 'Format IP Address tidak valid'
              }
            ]}
          >
            <Input placeholder="192.168.1.1 atau ::1" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
          >
            <Input.TextArea
              placeholder="Deskripsi IP (opsional)"
              rows={3}
            />
          </Form.Item>

          <Form.Item
            label="Status"
            name="is_active"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Batal
              </Button>
              <Button type="primary" htmlType="submit">
                {editingWhitelist ? 'Update' : 'Tambah'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Security;